import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Button, Table, Tooltip, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
// Api
import {
  GET_TICKET_EVENT_SALES_REPORT,
  GET_TICKETING_SETTING,
} from 'api/ticketing/queries';
// Types
import {
  GetTicketEventSalesReport,
  GetTicketEventSalesReportVariables,
} from 'api/ticketing/types/GetTicketEventSalesReport';
import { GetTicketingSetting } from 'api/ticketing/types/GetTicketingSetting';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import { TicketTierFields } from 'api/ticketing/types/TicketTierFields';
// Helpers
import {
  formatCount,
  formatEventDateTime,
  formatMoney,
  getAddTierAvailability,
  getTicketPriceLimits,
} from 'pages/Events/helpers';
// UI
import PageToolbar from 'ui/PageToolbar/PageToolbar';
// Components
import AddTierModal from '../AddTierModal/AddTierModal';
// Styles
import styles from './TiersTab.module.scss';

const { Text } = Typography;

type TiersTabProps = {
  event: TicketEventDetailsFields;
  tiers: TicketTierFields[];
  onChanged: () => void;
};

const TiersTab = ({ event, tiers, onChanged }: TiersTabProps): JSX.Element => {
  const [addOpen, setAddOpen] = useState(false);
  const { data: reportData, refetch: refetchReport } = useQuery<
    GetTicketEventSalesReport,
    GetTicketEventSalesReportVariables
  >(GET_TICKET_EVENT_SALES_REPORT, {
    variables: { input: { ticketEventId: event.id } },
    fetchPolicy: 'cache-and-network',
  });
  const { data: settingData } = useQuery<GetTicketingSetting>(
    GET_TICKETING_SETTING
  );

  const salesByTier = new Map(
    (reportData?.ticketEventSalesReport.byTier || []).map((row) => [
      row.tierId,
      row,
    ])
  );
  const availability = getAddTierAvailability(event.status);
  const disabledReason = !event.ticketsEnabled
    ? 'This event doesn’t sell tickets. Turn tickets on from Edit first.'
    : availability.reason;
  const at = (value?: string | null) =>
    value ? formatEventDateTime(value, event.timezone) : 'Event window';

  return (
    <>
      <PageToolbar>
        <Tooltip title={disabledReason}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!!disabledReason}
            onClick={() => setAddOpen(true)}
          >
            Add tier
          </Button>
        </Tooltip>
      </PageToolbar>

      {!event.ticketsEnabled && (
        <Alert
          type="info"
          showIcon
          message="This event is listed without tickets."
          className={styles.alert}
        />
      )}

      <Table<TicketTierFields>
        rowKey="id"
        dataSource={tiers}
        pagination={false}
        scroll={{ x: 960 }}
        locale={{ emptyText: 'No tiers yet.' }}
        columns={[
          {
            title: 'Tier',
            key: 'label',
            render: (_, tier) => (
              <div>
                <Text strong>{tier.label}</Text>
                {tier.description && (
                  <div>
                    <Text type="secondary">{tier.description}</Text>
                  </div>
                )}
              </div>
            ),
          },
          {
            title: 'Price',
            key: 'price',
            render: (_, tier) => formatMoney(tier.price),
          },
          {
            title: 'Sold',
            key: 'sold',
            render: (_, tier) => {
              const sales = salesByTier.get(tier.id);

              return `${
                sales ? formatCount(sales.ticketsSold) : '—'
              } / ${formatCount(tier.capacity)}`;
            },
          },
          {
            title: 'Comps',
            key: 'comps',
            render: (_, tier) => salesByTier.get(tier.id)?.compTickets ?? '—',
          },
          {
            title: 'Available',
            key: 'available',
            render: (_, tier) => salesByTier.get(tier.id)?.available ?? '—',
          },
          {
            title: 'Max per order',
            dataIndex: 'maxPerOrder',
            key: 'maxPerOrder',
          },
          {
            title: 'Sales window',
            key: 'window',
            render: (_, tier) => (
              <div>
                <div>{at(tier.salesOpenAt)}</div>
                {tier.salesCloseAt && (
                  <Text type="secondary">to {at(tier.salesCloseAt)}</Text>
                )}
              </div>
            ),
          },
        ]}
      />

      <AddTierModal
        event={event}
        tiers={tiers}
        {...getTicketPriceLimits(settingData?.getTicketingSetting)}
        visible={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={() => {
          setAddOpen(false);
          refetchReport();
          onChanged();
        }}
      />
    </>
  );
};

export default TiersTab;
